import { useSuspenseRolesQuery } from '#api/roles.queries.ts';
import { DEFAULT_ROLES_PAGE_SIZE } from '#api/roles.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { SMALLEST_PAGE_SIZE, TableFooter } from '#components/patterns/table/table-footer/TableFooter.tsx';
import { environmentVariables } from '#global/styles/environment-variables.json';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { useSavedPageSize } from '#hooks/useSavedPageSize.tsx';
import { useScrollbarWidth } from '#hooks/useScrollbarWidth.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { EmptyRoles } from './empty/EmptyRoles.tsx';
import { RolesList } from './roles-list/RolesList.tsx';

/**
 * {@link RolesPage} is the top-level component for the roles table page.
 */
export function RolesPage() {
  const [pageSize, setPageSize] = useSavedPageSize({ defaultPageSize: DEFAULT_ROLES_PAGE_SIZE });
  const [cursor, setCursor] = useUrlParam('cursor');
  const scrollbarWidth = useScrollbarWidth('#page-main');
  const adjustForScrollbarWidth = useMatchMedia(`(max-width: ${environmentVariables['--size-breakpoint-s']})`);
  const {
    data: { data: roles, metadata },
    isFetching,
  } = useSuspenseRolesQuery(cursor, pageSize);

  const hasRoles = Boolean(roles.length);
  if (!hasRoles) return <EmptyRoles addRoleHref="add" />;

  return (
    <TableLayout
      header={
        <PageHeader
          title="Roles"
          subtitle={
            <div
              id="roles-banner"
              style={
                adjustForScrollbarWidth ?
                  { width: `calc(100vw - 32px - ${scrollbarWidth}px`, paddingTop: 16 }
                : { paddingTop: 16 }
              }
            >
              <Banner variant="info" className="p">
                Roles control how hosts, lighthouses, and relays communicate through firewall rules. For more
                information, read{' '}
                <a
                  className="a"
                  href="https://www.defined.net/docs/guides/creating-firewalls-using-roles/"
                  target="_blank"
                  rel="help"
                >
                  Creating Firewalls Using Roles
                </a>
                .
              </Banner>
            </div>
          }
        >
          <ButtonGroup>
            <ButtonGroupItem>
              <LinkButton size="small" to="add">
                Add
              </LinkButton>
            </ButtonGroupItem>
          </ButtonGroup>
        </PageHeader>
      }
      pagination={
        metadata.totalCount > SMALLEST_PAGE_SIZE ?
          <TableFooter
            entityType="roles"
            pageSize={pageSize}
            metadata={metadata}
            isFetching={isFetching}
            setCursor={setCursor}
            onPageSizeChange={setPageSize}
          />
        : null
      }
    >
      <RolesList data={roles} isFetching={isFetching} />
    </TableLayout>
  );
}
